/**
 * cortex-admin の data-viewer API contract（zod schema + 型）。
 * API（apps/api/admin）と Web（apps/web/cortex-admin）が共有する。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business data-viewer のジョブ一覧 / 実行 / export / 登録 / 更新 / 削除 API ↔ Web 型を共有し、二重定義を排除する。
 * @graph-connects none
 */
import { z } from 'zod';
/**
 * DataViewerからCortexのcorporation Firestoreを選択するDB名。
 * @graph-connects firestore [reads_from, via:CORTEX_FIRESTORE_DATABASE] corporation databaseの選択エイリアス
 */
export const CORTEX_FIRESTORE_DATABASE = 'Cortex Firestore';
/**
 * フィルタ可能カラム1件（カラム名 + データ型）。実行画面のフィルタ入力欄がこれを元に作られる。
 *
 * `table` の有無で、実行時にどこへ条件がかかるかが変わる。
 *
 * - **`table` なし**: 実行結果に対する絞り込み。元 SQL を派生テーブルで包んで外側に `WHERE` を置く。
 *   `SELECT` に現れるカラムしか指定できない。
 * - **`table` あり**: 集約前の生データに対する絞り込み。そのテーブルと同名の CTE を前置して
 *   元 SQL のテーブル参照を差し替えるため、`GROUP BY` で集約される前の行に条件が効く。
 *   `SELECT` に出ていないカラム（`orders.status` など）も指定できる。
 *
 * @graph-connects none
 */
export const FilterableColumnSchema = z.object({
    column: z.string().min(1),
    type: z.enum(['date', 'number', 'string']),
    table: z.string().min(1).optional(),
});
/**
 * 実行時にサーバー側へ渡すフィルタ条件1件。
 *
 * API 側で SQL の `WHERE` 句へ変換し、値は driver のバインドパラメータとして渡す
 * （結果取得後の絞り込みではないので、200 行の表示上限の外にある行も対象になる）。
 *
 * - `values`: 値の列挙。`col IN (?, ?, ?)` になる。特定 ID を数件指定する用途
 * - `dateFrom` / `dateTo`: 日付範囲（`YYYY-MM-DD`）。`dateTo` は当日を含む
 * - `numberMin` / `numberMax`: 数値範囲（inclusive）
 *
 * 同じカラムに列挙と範囲を併記した場合は `AND` で両方効く。どれも未指定の条件は句を生成しない。
 * `column` は登録済み `filterableColumns` に含まれるものだけ許可する（API 側で検証）。
 *
 * @graph-connects none
 */
export const FilterConditionInputSchema = z.object({
    column: z.string().min(1),
    type: z.enum(['date', 'number', 'string']),
    values: z.array(z.string()).optional(),
    dateFrom: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, '日付は YYYY-MM-DD 形式で指定してください')
        .optional(),
    dateTo: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, '日付は YYYY-MM-DD 形式で指定してください')
        .optional(),
    numberMin: z.string().optional(),
    numberMax: z.string().optional(),
});
/**
 * 集約前の生データに対するフィルタ条件 1 件。
 *
 * {@link FilterConditionInputSchema} に対象テーブルを足したもの。
 *
 * 通常の `filters` は元 SQL を派生テーブルで包んで外側に `WHERE` を置くため、`GROUP BY` を
 * 含む SQL では「集約後の結果」に条件がかかる。`SELECT user_id, COUNT(*) FROM orders GROUP BY user_id`
 * を `created_at` で絞りたくても、外側には `created_at` が無い。
 *
 * `rawFilters` はテーブルごとに `WITH <table> AS (SELECT * FROM <table> WHERE ...)` を
 * 組み立てて登録 SQL の前に置く。同名 CTE が元のテーブル参照を差し替えるので、集約される前の
 * 行に条件が効く。対象カラムは登録済み `filterableColumns` ではなく、DESCRIBE で得た
 * テーブルの実カラムから選ぶ。
 *
 * @graph-connects none
 */
export const RawFilterConditionInputSchema = FilterConditionInputSchema.extend({
    table: z.string().min(1),
});
/** data-viewer ジョブ一覧アイテム。 @graph-connects none */
export const DataViewerJobSchema = z.object({
    id: z.string(),
    label: z.string(),
    description: z.string().optional(),
    datasetId: z.string().optional(),
    filterableColumns: z.array(FilterableColumnSchema).optional(),
});
/** data-viewer ジョブ一覧レスポンス。 @graph-connects none */
export const DataViewerJobListResponseSchema = z.object({
    jobs: z.array(DataViewerJobSchema),
    total: z.number(),
});
/** data-viewer job ID input。 @graph-connects none */
export const DataViewerJobIdInputSchema = z.object({
    id: z.string().regex(/^[a-zA-Z0-9_]+$/, 'ジョブIDは英数字とアンダースコアのみ使用できます'),
});
/**
 * クエリ実行 / エクスポートの input。
 *
 * `filters` は **optional**。省略時はフィルタなしで実行し、従来と同じ SQL を投げる
 * （Web の更新が API より遅れてデプロイされても壊れないようにするため）。
 *
 * @graph-connects none
 */
export const RunDataViewerJobInputSchema = z.object({
    id: DataViewerJobIdInputSchema.shape.id,
    filters: z.array(FilterConditionInputSchema).optional(),
    rawFilters: z.array(RawFilterConditionInputSchema).optional(),
    /** Firestore の次ページを取得するカーソル。 */
    cursor: z.string().max(4096).optional(),
});
/** query 実行結果。 @graph-connects none */
export const QueryResultSchema = z.object({
    columns: z.array(z.string()),
    rows: z.array(z.record(z.string(), z.unknown())),
    total: z.number(),
    /** さらに読み込める Firestore 結果がある場合の次ページカーソル。 */
    nextCursor: z.string().max(4096).optional(),
    /** Firestore CSV出力が最大行数で打ち切られた場合に true。 */
    truncated: z.boolean().optional(),
});
/** data-viewer ジョブ詳細。 @graph-connects none */
export const DataViewerJobDetailSchema = z.object({
    id: z.string(),
    label: z.string(),
    sql: z.string(),
    datasetId: z.string().optional(),
    description: z.string().optional(),
    allowedEmails: z.array(z.string()).optional(),
    allowedGroups: z.array(z.string()).optional(),
    filterableColumns: z.array(FilterableColumnSchema).optional(),
});
/** data-viewer ジョブ更新リクエスト。 @graph-connects none */
export const UpdateDataViewerJobRequestSchema = z.object({
    label: z.string().min(1),
    sql: z.string().min(1),
    datasetId: z.string().optional(),
    description: z.string().optional(),
    allowedEmails: z.array(z.email()).optional(),
    allowedGroups: z.array(z.string()).optional(),
    filterableColumns: z.array(FilterableColumnSchema).optional(),
});
/** data-viewer ジョブ作成リクエスト。 @graph-connects none */
export const CreateDataViewerJobRequestSchema = z.object({
    id: z.string().regex(/^[a-zA-Z0-9_]+$/, 'ジョブIDは英数字とアンダースコアのみ使用できます'),
    label: z.string().min(1),
    sql: z.string().min(1),
    datasetId: z.string().optional(),
    description: z.string().optional(),
    allowedEmails: z.array(z.email()).optional(),
    allowedGroups: z.array(z.string()).optional(),
    filterableColumns: z.array(FilterableColumnSchema).optional(),
});
/** data-viewer ジョブ ID レスポンス。 @graph-connects none */
export const DataViewerJobIdResponseSchema = z.object({
    id: z.string(),
});
/** DB 一覧レスポンス。 @graph-connects none */
export const DatabaseListResponseSchema = z.object({
    databases: z.array(z.string()),
});
/**
 * クエリ登録/更新前のプレビュー実行リクエスト。
 * 登録画面が filterableColumns の妥当性（実データに対して整合するか）を、SQL 実行前には
 * 未知の結果カラムに対して検証するために、送信時点で SQL を試験実行する。
 * @graph-connects none
 */
export const PreviewJobQueryRequestSchema = z.object({
    sql: z.string().min(1),
    datasetId: z.string().min(1),
});
/**
 * 生データフィルタが参照できるテーブル 1 件。
 *
 * 登録 SQL が `FROM` / `JOIN` で参照している実テーブルを API 側で抽出したもの。
 *
 * @graph-connects none
 */
export const DataViewerTableSchema = z.object({
    name: z.string(),
    schema: z.string().nullable(),
    alias: z.string().nullable(),
    /**
     * 生データフィルタの対象にできるか。
     * スキーマ修飾されたテーブル（`aircloset.orders`）は同名 CTE で差し替えられないため false。
     */
    filterable: z.boolean(),
});
/** 参照テーブル一覧レスポンス。 @graph-connects none */
export const DataViewerTableListResponseSchema = z.object({
    tables: z.array(DataViewerTableSchema),
    /** 集約前の生データに絞り込む必要がある SQL か（`GROUP BY` / `FROM (サブクエリ)` を含む） */
    requiresRawDataFilter: z.boolean(),
});
/** DESCRIBE で得たカラム 1 件。 @graph-connects none */
export const DataViewerTableColumnSchema = z.object({
    /** カラム名（DESCRIBE の Field） */
    field: z.string(),
    /** DB が返した型表記（DESCRIBE の Type。例: `varchar(255)`） */
    dbType: z.string(),
    /** dbType から判定したフィルタ入力の種別 */
    filterType: z.enum(['date', 'number', 'string']),
});
/**
 * 参照テーブル一覧のプレビュー取得リクエスト。
 *
 * 登録画面は保存前の SQL に対してテーブル候補を出す必要があるため、ジョブ ID ではなく
 * SQL と接続先を直接受ける（{@link PreviewJobQueryRequestSchema} と同じ考え方）。
 *
 * @graph-connects none
 */
export const PreviewQueryTablesRequestSchema = z.object({
    sql: z.string().min(1),
    datasetId: z.string().min(1),
});
/** テーブルのカラム一覧のプレビュー取得リクエスト。 @graph-connects none */
export const PreviewTableColumnsRequestSchema = z.object({
    sql: z.string().min(1),
    datasetId: z.string().min(1),
    table: z.string().min(1),
});
/** テーブルのカラム一覧レスポンス。 @graph-connects none */
export const DescribeDataViewerTableResponseSchema = z.object({
    columns: z.array(DataViewerTableColumnSchema),
});
//# sourceMappingURL=data-viewer.js.map